import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {
	IPubNubClientService,
	IPubNubEvent,
} from "data/services/pub_nub/pub_nub_client.servise";
import type {ISnackbarStore, ISnackBarContent} from "data/stores/snackbar/snackbar.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IFriendsStore} from "data/stores/friends/friends.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {toArray} from "lodash";
import type {IH2hStore} from "data/stores/h2h/h2h.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {useNavigate} from "react-router-dom";

enum ActionEvent {
	INVITATION_RECEIVED = "invitation_received",
	ACCEPTED = "accepted",
	DELETED = "deleted",
	ADDED = "added",
}

interface IFriendPubNub {
	event: ActionEvent;
	friendId: number;
	friendDisplayName: string;
}

interface IChallengePubNub {
	event: ActionEvent;
	opponentId: number;
	opponentDisplayName: string;
}

interface IParams {
	navigate: ReturnType<typeof useNavigate>;
}
export type IPubNubHocController = ViewController<IParams>;

@injectable()
export class PubNubHocController implements IPubNubHocController {
	private _navigate?: IParams["navigate"];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PubNubClientService)
		public readonly _pubNubClientService: IPubNubClientService,
		@inject(Bindings.SnackbarStore) public readonly _snackbarStore: ISnackbarStore,
		@inject(Bindings.UserStore) public readonly _userStore: IUserStore,
		@inject(Bindings.FriendsStore) public readonly _friendsStore: IFriendsStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.ContestsStore) public readonly _contestsStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	private get contestId() {
		return this._contestsStore.currentContest?.id;
	}

	init(param: IParams) {
		this._navigate = param.navigate;
		if (this.userChannels) {
			this._pubNubClientService.subscribe({
				channels: [...toArray(this.userChannels)],
			});

			this._pubNubClientService.listen<IFriendPubNub>(this.actionFriends);
			this._pubNubClientService.listen<IChallengePubNub>(this.actionChallenge);
		}
	}

	private actionChallenge = (event: IPubNubEvent<IChallengePubNub> | null) => {
		const channels = this.userChannels;
		const contestId = this.contestId;
		if (!contestId || !channels || !event?.channel.includes(channels.challenge)) {
			return;
		}

		const message = event?.message;
		if (message) {
			const challengeAction = {
				[ActionEvent.INVITATION_RECEIVED]: () => {
					void this._h2hStore.getInvitations(contestId);
				},
				[ActionEvent.ACCEPTED]: () => {
					void this._h2hStore.getChallenges(contestId);
					void this._h2hStore.getInvitations(contestId);
				},
				[ActionEvent.DELETED]: () => {
					void this._h2hStore.getChallenges(contestId);
					void this._h2hStore.getInvitations(contestId);
				},
				[ActionEvent.ADDED]: () => {
					void this._h2hStore.getChallenges(contestId);
					void this._h2hStore.getInvitations(contestId);
				},
			}[message.event];

			challengeAction?.();
			this.showChallengeSnackbarMessage(message);
		}
	};

	private showChallengeSnackbarMessage(data: IChallengePubNub) {
		const message = {
			[ActionEvent.DELETED]: this.i18n.t(
				"pub_nub.challenge.user_has_left",
				`{{X}} has left from your challenge list`,
				{X: data.opponentDisplayName}
			),
			[ActionEvent.INVITATION_RECEIVED]: this.i18n.t(
				"pub_nub.challenge.received_invite",
				`You have got an invite from {{X}}`,
				{X: data.opponentDisplayName}
			),
			[ActionEvent.ACCEPTED]: this.i18n.t(
				"pub_nub.challenge.accepted_invite",
				`{{X}} has accepted your challenge invite`,
				{X: data.opponentDisplayName}
			),
			[ActionEvent.ADDED]: this.i18n.t(
				"pub_nub.challenge.added_invite",
				`{{X}} has been added to your challenge invite`,
				{X: data.opponentDisplayName}
			),
		}[data.event];

		this._snackbarStore.openSnackBar({
			message,
		});
	}

	private actionFriends = (event: IPubNubEvent<IFriendPubNub> | null) => {
		const channels = this.userChannels;
		if (!channels || !event?.channel.includes(channels.friends)) {
			return;
		}

		const message = event?.message;
		if (message) {
			const friendsAction = {
				[ActionEvent.INVITATION_RECEIVED]: () => {
					void this._friendsStore.fetchInvites().then((invites) => {
						if (invites.length > 1) {
							this._snackbarStore.openSnackBar({
								message: this.i18n.t(
									"pub_nub.friends.multiple_invites",
									`You have multiple friend invitations!`
								),
								callback: () => {
									this.toFriendsPage();
								},
							});
							return;
						}

						this.showFriendsSnackbarMessage(message);
					});
				},
				[ActionEvent.ACCEPTED]: () => {
					this._friendsStore.moveToFriend(message.friendId);
					void this._friendsStore.fetchInvites();
					void this._friendsStore.fetch();
					this.showFriendsSnackbarMessage(message);
				},
				[ActionEvent.DELETED]: () => {
					this._friendsStore.deleteFromFriendLists(message.friendId);
					this.showFriendsSnackbarMessage(message);
				},
				[ActionEvent.ADDED]: () => {
					void this._friendsStore.fetch().then(() => {
						this.showFriendsSnackbarMessage(message);
					});
					this.showFriendsSnackbarMessage(message);
				},
			}[message.event];

			friendsAction?.();
		}
	};

	private showFriendsSnackbarMessage(data: IFriendPubNub) {
		const dataByEvent: ISnackBarContent = {
			[ActionEvent.DELETED]: {
				message: this.i18n.t(
					"pub_nub.friends.user_has_left",
					`{{X}} has left from your friend list`,
					{X: data.friendDisplayName}
				),
			},
			[ActionEvent.INVITATION_RECEIVED]: {
				message: this.i18n.t(
					"pub_nub.friends.received_invite",
					`You have got an invite from {{X}}`,
					{X: data.friendDisplayName}
				),
				callback: () => {
					this.toFriendsPage();
				},
			},
			[ActionEvent.ACCEPTED]: {
				message: this.i18n.t(
					"pub_nub.friends.accepted_invite",
					`{{X}} has accepted your friend invite`,
					{X: data.friendDisplayName}
				),
				callback: () => {
					this.toFriendsPage();
				},
			},
			[ActionEvent.ADDED]: {
				message: this.i18n.t(
					"pub_nub.friends.added_invite",
					`{{X}} has been add to your friend list`,
					{X: data.friendDisplayName}
				),
				callback: () => {
					this.toFriendsPage();
				},
			},
		}[data.event];

		this._snackbarStore.openSnackBar({...dataByEvent});
	}

	private get userChannels() {
		const userId = this._userStore.user?.id;
		if (userId) {
			return {
				friends: `mlb-fantasy-spinball-user-${userId}-friend-event`,
				challenge: `mlb-fantasy-spinball-user-${userId}-challenge-event`,
			};
		}
		return null;
	}

	private toFriendsPage = () => {
		this._navigate?.("/head_to_head/friends");
	};
}
